import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { Terminal as XTerm } from '@xterm/xterm';
import { getTerminalTheme } from './theme';
import { workbenchStore } from '~/lib/stores/workbench';

const terminalInstances = new Map<string, TerminalInstance>();

export interface CreateOptions {
  id?: string;
  readonly?: boolean;
  webLinks?: boolean;
}

export interface TerminalInstance {
  terminal: XTerm;
  dispose(): void;
  attach(parent: HTMLElement): void;
}

export function createXTerm({ id, readonly, webLinks }: CreateOptions = {}): TerminalInstance {
  if (id && terminalInstances.has(id)) {
    return terminalInstances.get(id)!;
  }

  const fitAddon = new FitAddon();

  const terminal = new XTerm({
    cursorBlink: true,
    convertEol: true,
    disableStdin: readonly,
    theme: getTerminalTheme(readonly ? { cursor: '#00000000' } : {}),
    fontSize: 12,
    fontFamily: 'Menlo, courier-new, courier, monospace',
  });

  terminal.loadAddon(fitAddon);

  if (webLinks) {
    terminal.loadAddon(new WebLinksAddon());
  }

  /**
   * Fix for scroll events firing just before terminal is rendered
   * because of the 0px height of the resizable container.
   */
  const unsubscribeShowTerminal = workbenchStore.showTerminal.listen(() => {
    requestAnimationFrame(() => {
      terminal.scrollToBottom();
    });
  });

  const resizeObserver = new ResizeObserver(() => {
    fitAddon.fit();
  });

  let observedElement: HTMLElement | undefined;

  const instance = {
    terminal,
    dispose: () => {
      resizeObserver.disconnect();

      terminal.dispose();

      unsubscribeShowTerminal();

      if (id) {
        terminalInstances.delete(id!);
      }
    },
    attach: (parent: HTMLElement) => {
      if (!terminal.element) {
        // if there is no element yet on the terminal, we can safely call open
        terminal.open(parent);

        resizeObserver.observe(parent);

        observedElement = parent;

        fitAddon.fit();

        terminal.refresh(0, terminal.rows - 1);
      } else {
        // if there is already an element, open was already called and we need to move it
        parent.appendChild(terminal.element);

        if (observedElement) {
          resizeObserver.unobserve(observedElement);
        }

        resizeObserver.observe(parent);

        observedElement = parent;

        // we call fit and refresh after the browser has committed the DOM change
        requestAnimationFrame(() => {
          fitAddon.fit();

          terminal.refresh(0, terminal.rows - 1);
        });
      }
    },
  };

  if (id) {
    terminalInstances.set(id, instance);
  }

  return instance;
}
